import { Box, Paper } from '@mantine/core';
import { SearchResultStateProvider } from '../hoc/EventSearch/SearchResults.hooks';
import { SeatingChartProvider } from '../hoc/SeatingChart/SeatingChart.hooks';
import { RuleStateProvider } from '../data/Rule.state';
import { BulletinModal } from '../hoc/Bulletin/BulletinModal';
import { InviteModal } from '../hoc/InviteModal/InviteModal';
import { useLocalStorage } from '@mantine/hooks';
import { VersionNotificationBanner } from '../hoc/Banners/VersionNotificationBanner';
import { SupportActivationBanner } from '../hoc/Banners/SupportActivationBanner';
import { BulkStateProvider, useBulkState } from '../data/Bulk.state';
import { BulkExitOverlay } from '../components/Overlay/BulkExitOverlay';
import { BulkOperationLoadingMask } from '../components/Overlay/BulkOperationLoadingMask';
import { PlanExpirationBanner } from '../hoc/Banners/PlanExpirationBanner';
import { UnpaidInvoiceBanner } from '../hoc/Banners/UnpaidInvoiceBanner';
import { EventPerformance } from '../hoc/EventPerformance/EventPerformance';
import { IntegrationProblemBanner } from '../hoc/Banners/IntegrationProblemBanner';
import { EventPerformanceProvider } from '../hoc/EventPerformance/EventPerformance.hooks';
import { ListingStateProvider } from '../data/Listing.state';
import cx from 'clsx';
import { useFlag } from '@unleash/proxy-client-react';
import { HomeStateProvider, useHomeState } from './Home.hooks';
import { PricerView } from '../types';
import { isDtiHosted } from '../utils/whitelabel-consts';
import { DTIInventoryStateProvider } from '../hoc/DTI/DTI.Inventory.hooks';
import { DTIModals } from '../hoc/DTI/DTIModals';
import { NowPricing } from '../hoc/NowPricing/NowPricing';
import { SeatingChart } from '../hoc/SeatingChart/SeatingChart';
import { BNSidebar } from '../hoc/NavBar/Sidebar';
import { Search } from '../hoc/EventSearch/Search';
import { SearchResults } from '../hoc/EventSearch/SearchResults';
import { Inventory } from '../hoc/Inventory/Inventory';
import { MarketListings } from '../hoc/MarketListings/MarketListings';
import { useGlobalState } from '../data/Global.state';
import { Allotment, AllotmentHandle, LayoutPriority } from 'allotment';
import { FiltersPreview } from '../hoc/SeatingChart/Filters/FiltersPreview';
import { CollapseOverlay } from '../hoc/SeatingChart/SeatingChart.CollapseOverlay';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { OfferRule } from '../hoc/Enterprise/InTuneAnalytics/OfferRule';
import { VLModals } from '../hoc/VictoryLive/VLModals';
import { useAtomValue } from 'jotai';
import { showEventPerformanceAtom } from '../data/atoms';

const COLLAPSED_FILTER_PANEL_WIDTH = 329;
const EXPANDED_FILTER_PANEL_WIDTH = 788;
const MIN_INVENTORY_PANEL_WIDTH = 450;

export default function Home() {
  return (
    <ListingStateProvider>
      <RuleStateProvider>
        <BulkStateProvider>
          <BulletinModal />
          <InviteModal />
          <div className="outer-appWrap">
            <VersionNotificationBanner />
            <SupportActivationBanner />
            <PlanExpirationBanner />
            <UnpaidInvoiceBanner />
            <IntegrationProblemBanner />
            <HomeStateProvider>
              {isDtiHosted ? (
                <DTIInventoryStateProvider>
                  <HomeInterior />
                  <DTIModals />
                </DTIInventoryStateProvider>
              ) : (
                <>
                  <HomeInterior />
                  <VLModals />
                </>
              )}
            </HomeStateProvider>
          </div>
        </BulkStateProvider>
      </RuleStateProvider>
    </ListingStateProvider>
  );
}

function HomeInterior() {
  const [showSidebar] = useLocalStorage({
    key: 'setting-sidebar',
    defaultValue: true,
    getInitialValueInEffect: false,
  });
  const [pricerLayout, setPricerLayout] = useLocalStorage<number[]>({
    key: 'setting-pricer-layout',
    defaultValue: [1132, 788],
    getInitialValueInEffect: false,
  });
  const collapsedFilterColumnFlag = useFlag('collapsed-filter-column');
  const { isTrendsCollapsed, handleTrendsToggle } = useHomeState('isTrendsCollapsed', 'handleTrendsToggle');
  const { pricerView } = useGlobalState('pricerView');
  const { showBulkOptions, isBulkActionLoading } = useBulkState('showBulkOptions', 'isBulkActionLoading');
  const { selectedTenantIds } = useGlobalState('selectedTenantIds');
  const showEventPerformance = useAtomValue(showEventPerformanceAtom);

  const allotmentRef = useRef<AllotmentHandle>(null!);
  const rightPaneWidthBeforePerformanceOpenRef = useRef<number | null>(null);

  function handleDragEnd(value: number[]) {
    if (collapsedFilterColumnFlag && value[1] < EXPANDED_FILTER_PANEL_WIDTH) {
      const totalWidth = value[0] + value[1];
      allotmentRef.current.resize([totalWidth - COLLAPSED_FILTER_PANEL_WIDTH, COLLAPSED_FILTER_PANEL_WIDTH]);
      document.body.classList.remove('show-market-filters');
    }
  }

  function handleFiltersClick() {
    const totalWidth = pricerLayout[0] + pricerLayout[1];
    allotmentRef.current.resize([totalWidth - EXPANDED_FILTER_PANEL_WIDTH, EXPANDED_FILTER_PANEL_WIDTH]);
  }

  useEffect(() => {
    if (collapsedFilterColumnFlag && pricerLayout[1] < EXPANDED_FILTER_PANEL_WIDTH) {
      const totalWidth = pricerLayout[0] + pricerLayout[1];
      allotmentRef.current.resize([totalWidth - COLLAPSED_FILTER_PANEL_WIDTH, COLLAPSED_FILTER_PANEL_WIDTH]);
      document.body.classList.remove('show-market-filters');
    }
  }, [collapsedFilterColumnFlag, pricerLayout]);

  useLayoutEffect(() => {
    if (!collapsedFilterColumnFlag) {
      rightPaneWidthBeforePerformanceOpenRef.current = null;
      return;
    }

    const [leftPaneWidth, rightPaneWidth] = pricerLayout;
    const totalWidth = leftPaneWidth + rightPaneWidth;
    const expandedRightPaneWidth = Math.min(EXPANDED_FILTER_PANEL_WIDTH, Math.max(COLLAPSED_FILTER_PANEL_WIDTH, totalWidth - MIN_INVENTORY_PANEL_WIDTH));

    if (showEventPerformance && rightPaneWidth < EXPANDED_FILTER_PANEL_WIDTH && rightPaneWidthBeforePerformanceOpenRef.current === null) {
      rightPaneWidthBeforePerformanceOpenRef.current = rightPaneWidth;
      allotmentRef.current?.resize([totalWidth - expandedRightPaneWidth, expandedRightPaneWidth]);
      document.querySelector('.appRight')?.classList.remove('show-market-filters');
      return;
    }

    if (!showEventPerformance && rightPaneWidthBeforePerformanceOpenRef.current !== null) {
      const restoredRightPaneWidth = rightPaneWidthBeforePerformanceOpenRef.current;
      rightPaneWidthBeforePerformanceOpenRef.current = null;
      allotmentRef.current?.resize([totalWidth - restoredRightPaneWidth, restoredRightPaneWidth]);
    }
  }, [collapsedFilterColumnFlag, pricerLayout, showEventPerformance]);

  const hasInTuneOffersFlag = useFlag('intune-offer-info') || (selectedTenantIds.length === 1 && selectedTenantIds.every((t) => t === 'deac90fd-337e-4ef5-9f11-954437fd9298'));

  return (
    <div className="inner-appWrap">
      <Allotment
        ref={allotmentRef}
        proportionalLayout={false}
        onDragEnd={(value) => handleDragEnd(value)}
        defaultSizes={pricerLayout}
        onChange={(value) => setPricerLayout(value)}
        separator={false}
      >
        <Allotment.Pane minSize={450}>
          <div className={cx('appWrap appLeft', showSidebar ? 'sidebar-open' : 'sidebar-collapse')}>
            <div className="col1">
              <BNSidebar>
                {showBulkOptions && <BulkExitOverlay />}
                <Paper className="sidebarSearchWrapper" pos="relative">
                  <SearchResultStateProvider>
                    <Search />
                    {pricerView !== PricerView.Split && <SearchResults />}
                  </SearchResultStateProvider>
                </Paper>
              </BNSidebar>
            </div>
            <div className={cx('col2', pricerView === PricerView.Split ? 'stacked-grids' : null)}>
              <Inventory />
            </div>
          </div>
        </Allotment.Pane>
        {/* <Allotment.Pane minSize={collapsedFilterColumnFlag ? 329 : 788} maxSize={1288} preferredSize="10%"> */}
        <Allotment.Pane minSize={collapsedFilterColumnFlag ? COLLAPSED_FILTER_PANEL_WIDTH : EXPANDED_FILTER_PANEL_WIDTH} maxSize={1288} priority={LayoutPriority.Low}>
          <div className={cx('appRight', isTrendsCollapsed && 'trends-collapse', collapsedFilterColumnFlag && pricerLayout[1] === COLLAPSED_FILTER_PANEL_WIDTH && 'collapsed-filters')}>
            <div className="col3">
              <Paper className="seatingChartWrapper" pos="relative">
                <SeatingChartProvider>
                  <SeatingChart isTrendsCollapsed={isTrendsCollapsed} handleTrendsToggle={handleTrendsToggle} />
                </SeatingChartProvider>
              </Paper>
              {collapsedFilterColumnFlag && <CollapseOverlay />}
            </div>
            <div className="col4">
              <NowPricing />
              {hasInTuneOffersFlag && <OfferRule />}
              {collapsedFilterColumnFlag && <FiltersPreview clickFunction={handleFiltersClick} />}
              <MarketListings />
            </div>
            <EventPerformanceProvider>
              <EventPerformance />
            </EventPerformanceProvider>
            {showBulkOptions && (
              <Box className="bulk-overlay-right">
                <BulkExitOverlay />
              </Box>
            )}
          </div>
        </Allotment.Pane>
      </Allotment>
      {isBulkActionLoading && <BulkOperationLoadingMask />}
    </div>
  );
}
